/**
 * EmpresaRepository - Repositorio unificado para Empresas
 *
 * Implementación compartida entre web (Dexie) y mobile (SQLite).
 */

import { IEmpresaRepository } from '../interfaces/IEmpresaRepository';
import { BaseRepository } from './BaseRepository';
import { IDatabaseAdapter } from '../adapters/IDatabaseAdapter';
import { Empresa } from '../interfaces/entities';

/**
 * Repositorio unificado de Empresas
 */
export class EmpresaRepository extends BaseRepository<Empresa, string> implements IEmpresaRepository {

  constructor(db: IDatabaseAdapter) {
    super(db, 'empresas', 'empresas');
  }

  async add(data: Omit<Empresa, 'id'>): Promise<string> {
    const id = await this.getNextId();
    const now = this.getCurrentTimestamp();
    const record: Empresa = { ...data, id, created_at: data.created_at || now };
    await this.db.insert('empresas', record);
    this.emitSync('INSERT', record);
    return id;
  }

  async update(data: Partial<Empresa> & { id: string }): Promise<void> {
    await this.db.update('empresas', data.id, data);
    this.emitSync('UPDATE', data as any);
  }

  async delete(id: string): Promise<void> {
    await this.db.delete('empresas', id);
    this.emitSync('DELETE', id);
  }

  async getByNombre(nombre: string): Promise<Empresa[]> {
    return this.db.query<Empresa>('empresas', {
      field: 'nombre',
      operator: 'includes',
      value: nombre.toLowerCase()
    });
  }

  async getNextId(): Promise<string> {
    const all = await this.getAll();
    if (all.length === 0) return '1';

    const maxId = Math.max(...all.map(e => parseInt(e.id) || 0));
    return String(maxId + 1);
  }

  // ==================== Bulk Operations ====================

  async bulkAdd(items: Empresa[]): Promise<void> {
    await this.db.bulkInsert('empresas', items);
    this.emitBulkSync('BULK_INSERT', items);
  }

  async bulkAddSilent(items: Empresa[]): Promise<void> {
    await this.db.bulkInsert('empresas', items);
    items.forEach(item => this.emitLocalSync('INSERT', item));
  }

  async bulkPut(items: Empresa[]): Promise<void> {
    await this.db.bulkUpsert('empresas', items);
    this.emitBulkSync('BULK_INSERT', items);
  }

  async bulkPutSilent(items: Empresa[]): Promise<void> {
    await this.db.bulkUpsert('empresas', items);
    items.forEach(item => this.emitLocalSync('UPDATE', item));
  }

  async bulkDelete(ids: string[]): Promise<void> {
    await this.db.bulkDelete('empresas', ids);
    this.emitBulkSync('BULK_DELETE', ids);
  }

  async bulkDeleteSilent(ids: string[]): Promise<void> {
    await this.db.bulkDelete('empresas', ids);
    ids.forEach(id => this.emitLocalSync('DELETE', id));
  }

  async getLastByDate(dateColumn: string = 'created_at'): Promise<Empresa | undefined> {
    const results = await this.db.query<Empresa>('empresas', undefined, {
      column: dateColumn,
      direction: 'desc'
    });
    return results[0] || undefined;
  }
}
